// Ported from eb-ui `src/legacy/PageLayout/PageLayoutPreview.tsx` — the
// organizer PREVIEW device toggle (desktop/mobile) + the AutocreatedPill.
//   - `@eventbrite/legacy-infra.http` `setWindowLocation` →
//     `window.location.assign`.
//   - `@eventbrite/legacy-infra.i18n` → `@/lib/gettext`; icons →
//     `../lib/iconography` (ScreenLarge/ScreenSmall are not in the 142-icon
//     bridge subset → inlined).
// The `cc-preview*` classNames + the trackEvent seams are preserved verbatim.
import React, { useContext, useState } from 'react';

import { AutocreatedPill } from '../components/AutocreatedPill';
import { AnalyticsContext, ApplicationContext } from '../context';
import {
    AnalyticsAction,
    AnalyticsCategory,
    isAutocreatedCollectionPreviewWithTips,
    trackEvent,
} from '../_shared';
import { Icon } from '../lib/iconography';
import { ScreenLarge, ScreenSmall } from '../lib/iconography';
import { gettext } from '@/lib/gettext';
import type { CollectionServerData } from '@/types';

enum PreviewType {
    mobile = 'mobile',
    desktop = 'desktop',
}

export const PageLayoutPreview: React.FC<{ children?: React.ReactNode }> = ({
    children,
}) => {
    const [preview, setPreview] = useState(PreviewType.desktop);

    const { collectionId } = useContext(AnalyticsContext);
    const context = useContext(ApplicationContext);
    const collectionData = context as CollectionServerData | null;

    const previewAutocreated =
        (isAutocreatedCollectionPreviewWithTips(context) &&
            collectionData?.collection?.is_autocreated) ||
        false;

    const onChangeDeviceClick = (type: PreviewType) => {
        setPreview(type);
        trackEvent({
            action: AnalyticsAction.DevicePreview,
            category: AnalyticsCategory.Create,
            label: type,
        });
    };

    // The preview-collection class (full, static — extracted so the className
    // attribute carries only whole class names, matching the eb-ui markup).
    const collectionClassName =
        preview === 'mobile'
            ? 'cc-preview-collection cc-preview-collection--mobile'
            : 'cc-preview-collection cc-preview-collection--desktop';

    return (
        <div className="cc-preview">
            <header aria-labelledby="heading" className="cc-preview__header">
                <div
                    id="heading"
                    className="eds-bg-color--background eds-text-bl eds-l-pad-all-4"
                >
                    {previewAutocreated
                        ? gettext('Your example collection')
                        : gettext('Collection preview')}
                </div>
                <button
                    onClick={() => {
                        if (previewAutocreated) {
                            window.location.assign('/organizations/collections');
                        } else {
                            window.close();
                        }
                    }}
                    className="cc-preview__close-button eds-text-bm"
                >
                    {previewAutocreated
                        ? gettext('Back to collections')
                        : gettext('Close preview')}
                </button>
                <div className="cc-preview-nav">
                    <ul className="cc-preview-nav__list">
                        <li className="cc-preview-nav__item">
                            <button
                                disabled={preview === 'desktop'}
                                className="cc-preview-nav__button"
                                onClick={() => {
                                    onChangeDeviceClick(PreviewType.desktop);
                                }}
                            >
                                <Icon
                                    type={<ScreenLarge />}
                                    title={gettext('Show desktop preview')}
                                    size="small"
                                />
                            </button>
                        </li>
                        <li className="cc-preview-nav__item">
                            <button
                                disabled={preview === 'mobile'}
                                className="cc-preview-nav__button"
                                onClick={() => {
                                    onChangeDeviceClick(PreviewType.mobile);
                                }}
                            >
                                <Icon
                                    type={<ScreenSmall />}
                                    title={gettext('Show mobile preview')}
                                    size="small"
                                />
                            </button>
                        </li>
                    </ul>
                </div>
            </header>
            <main className="cc-preview__main-content">
                {previewAutocreated && (
                    <AutocreatedPill collectionId={collectionId} />
                )}
                <div
                    data-testid="preview-collection"
                    className={collectionClassName}
                >
                    {children}
                </div>
            </main>
        </div>
    );
};
